import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, ChevronDown, ChevronUp, ChevronRight, ArrowUpRight, ArrowDownRight, Minus, FlaskConical, Search, SlidersHorizontal, Eye, ShoppingCart, CreditCard, ReceiptText, TrendingUp, RotateCcw, Play, Waypoints, PackageSearch, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { GChartCanvas, useChartTheme, GChipLegend, smoothPath } from '@/components/growth/chart-canvas';
import { channelColor } from '@/lib/channel-colors';
import { moneyShort } from '@/lib/money';
import { InfoTip } from '@/components/ui/info-tip';
import { ColumnHelp, TableHelp } from '@/components/ui/column-help';
import { THead } from '@/components/ui/table-kit';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { CardDeck } from '@/components/ui/card-deck';
import { ExplainButton } from '@/components/qbee/qbee-buttons';
import { Lede, MoreInfo } from '@/components/ui/calm';
import { api } from '@/lib/api';
import { useEmbedded } from '@/components/reports/embedded';
import { reportsV2Live } from '@/lib/reports-v2';
import { METRIC_DEFS } from '@/lib/metric-defs';
import { ResponsiveContainer, BarChart, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Area } from 'recharts';
import { CountUp } from '@/components/magic/count-up';

/**
 * The Growth section's shared visual language. Built after the verdict that
 * the first pass "presented data" without making anyone want to read it.
 * The rules, borrowed from the best tools in the category and our own
 * dashboards: a number is never alone (delta + shape beside it), magnitude
 * columns carry an in-cell bar so the eye ranks rows without reading, ratio
 * columns are heat-tinted so good and bad are pre-attentive, and every table
 * sorts by click because the user's question decides the order, not ours.
 */

// ── Sparkline: the shape beside (or under) the number ──────────────────────
// `fill` mode stretches to the container's width; vector-effect keeps the
// stroke crisp under the horizontal scaling.
export function Spark({ data = [], width = 96, height = 28, color = 'var(--spark, #2D63D8)', fill = false, avg = null, endColor = null }) {
  const path = useMemo(() => {
    const pts = (data || []).map((v) => (v == null ? null : Number(v)));
    const real = pts.filter((v) => v != null);
    if (real.length < 2) return null;
    const max = Math.max(...real), min = Math.min(...real);
    const span = max - min || 1;
    const step = width / (pts.length - 1);
    const y = (v) => height - 3 - ((v - min) / span) * (height - 6);
    const clampY = (v) => Math.max(2, Math.min(height - 2, v));
    // Catmull-Rom → cubic bezier for a smooth line. Gaps (null days) still
    // break the curve rather than lying with an interpolation: each
    // contiguous run of real days is smoothed on its own.
    const runs = [];
    let run = [];
    pts.forEach((v, i) => {
      if (v == null) { if (run.length) runs.push(run); run = []; return; }
      run.push({ x: i * step, y: y(v) });
    });
    if (run.length) runs.push(run);
    if (!runs.length) return null;
    let d = '';
    for (const r of runs) {
      d += `M${r[0].x.toFixed(1)},${r[0].y.toFixed(1)}`;
      for (let i = 0; i < r.length - 1; i++) {
        const p0 = r[i - 1] || r[i], p1 = r[i], p2 = r[i + 1], p3 = r[i + 2] || p2;
        const c1x = p1.x + (p2.x - p0.x) / 6, c1y = clampY(p1.y + (p2.y - p0.y) / 6);
        const c2x = p2.x - (p3.x - p1.x) / 6, c2y = clampY(p2.y - (p3.y - p1.y) / 6);
        d += `C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
      }
    }
    const lastRun = runs[runs.length - 1];
    const lastP = lastRun[lastRun.length - 1];
    return { d, area: `${d}L${lastP.x.toFixed(1)},${height}L0,${height}Z`, lastX: lastP.x, lastY: lastP.y, avgY: avg != null ? y(avg) : null };
  }, [data, width, height, avg]);
  const gid = React.useId();
  if (!path) return null;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={fill ? undefined : width} height={fill ? undefined : height}
      preserveAspectRatio={fill ? 'none' : undefined}
      className={cn('overflow-visible', fill && 'h-full w-full')} aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity=".22" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={path.area} fill={`url(#${gid})`} />
      {path.avgY != null && path.avgY > 2 && path.avgY < height - 2 && (
        <line x1="0" x2={width} y1={path.avgY} y2={path.avgY} stroke={color} opacity="0.35"
          strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
      )}
      <path d={path.d} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={path.lastX} cy={path.lastY} r={endColor ? 3 : 2.4} fill={endColor || color} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ── Delta chip: coloured by whether the move is good for THIS metric ───────
export function Delta({ pct, goodDirection = 'up', className }) {
  if (pct == null) return <span className={cn('text-[13px] text-muted-foreground', className)}>-</span>;
  const up = pct > 0, flat = pct === 0;
  const good = goodDirection === 'none' ? null : goodDirection === 'up' ? up : !up && !flat;
  // v3: the colour is on the number, no pill ground behind it.
  const cls = good == null ? 'text-muted-foreground'
    : good ? 'text-emerald-700 dark:text-emerald-400'
      : 'text-rose-600 dark:text-rose-400';
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-[13px] font-semibold tabular-nums', cls, className)}>
      <Icon className="size-3" />{Math.abs(pct)}%
    </span>
  );
}

// ── Trend helpers ──────────────────────────────────────────────────────────
export function fmtMetric(kind, v, currency) {
  if (v == null) return '-';
  if (kind === 'money') return moneyShort(v, currency);
  if (kind === 'pct') return `${Math.round(v * 100) / 100}%`;
  if (kind === 'ratio') return `${Math.round(v * 100) / 100}x`;
  return Number(v).toLocaleString('en-IN');
}

// Recent momentum: last 7 days against the 7 before. Sums for money/counts,
// means for ratios (summing a ratio is a lie).
function sevenOverSeven(data, kind) {
  if (!data || data.length < 14) return null;
  const half = (arr) => {
    const real = arr.filter((v) => v != null);
    if (real.length < 3) return null;
    const s = real.reduce((a, b) => a + Number(b), 0);
    return kind === 'pct' || kind === 'ratio' ? s / real.length : s;
  };
  const prev = half(data.slice(0, 7)), last = half(data.slice(7, 14));
  if (prev == null || last == null || prev <= 0) return null;
  return Math.round(((last - prev) / prev) * 100);
}

// The endpoint against the 13 days before it: flag only real outliers.
export function endpointAnomaly(data, goodDirection) {
  if (!data || data.length < 14) return null;
  const base = data.slice(0, 13).filter((v) => v != null).map(Number);
  const last = data[13];
  if (last == null || base.length < 6) return null;
  const mean = base.reduce((a, b) => a + b, 0) / base.length;
  const sd = Math.sqrt(base.reduce((a, b) => a + (b - mean) ** 2, 0) / base.length);
  if (!(sd > 0) || !(mean > 0)) return null;
  const z = (Number(last) - mean) / sd;
  if (Math.abs(z) < 2) return null;
  const above = Number(last) > mean;
  const good = goodDirection === 'none' ? null : goodDirection === 'up' ? above : !above;
  return {
    good,
    pct: Math.round((Math.abs(Number(last) - mean) / mean) * 100),
    above,
  };
}

const DAY_FMT = { weekday: 'short', day: 'numeric', month: 'short' };

// ── TrendSpark: the full-width trend under a stat number ───────────────────
// Hoverable (day + value tooltip), dashed 14-day average line, yesterday's
// value printed in the corner, endpoint dot recoloured on a real outlier.
export function TrendSpark({ data = [], days = [], kind = 'count', currency, color = '#2D63D8', goodDirection = 'up', anomaly }) {
  const boxRef = useRef(null);
  const [hover, setHover] = useState(null);
  const real = (data || []).filter((v) => v != null);
  if (real.length < 2) return null;
  const last = data[data.length - 1];
  const avg = real.reduce((a, b) => a + Number(b), 0) / real.length;
  const onMove = (e) => {
    const r = boxRef.current?.getBoundingClientRect();
    if (!r || !r.width) return;
    const i = Math.max(0, Math.min(data.length - 1, Math.round(((e.clientX - r.left) / r.width) * (data.length - 1))));
    setHover({ i, x: (i / (data.length - 1)) * 100 });
  };
  const anomColor = anomaly ? (anomaly.good ? '#2D63D8' : '#D95F57') : null;
  return (
    <div className="mt-3">
      <div ref={boxRef} className="relative h-11 w-full cursor-crosshair" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <Spark data={data} color={color} height={44} width={220} fill avg={avg} endColor={anomColor} />
        {last != null && !hover && (
          <span className="pointer-events-none absolute right-0 top-0 rounded bg-card/80 px-1 text-[13px] font-semibold tabular-nums text-muted-foreground">
            {fmtMetric(kind, Number(last), currency)}
          </span>
        )}
        {hover && (
          <>
            <div className="pointer-events-none absolute inset-y-0 w-px bg-foreground/20" style={{ left: `${hover.x}%` }} />
            <span className={cn('pointer-events-none absolute top-0 z-10 whitespace-nowrap rounded-md border border-[hsl(var(--card-border))] bg-card px-1.5 py-0.5 text-[13px] font-semibold tabular-nums shadow-sm',
              hover.x > 55 ? '-translate-x-full' : '')} style={{ left: `${hover.x}%` }}>
              {days[hover.i] ? new Date(`${days[hover.i]}T00:00:00`).toLocaleDateString('en-GB', DAY_FMT) : `day ${hover.i + 1}`}
              {' · '}
              {data[hover.i] != null ? fmtMetric(kind, Number(data[hover.i]), currency) : 'no data'}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// ── Stat tile: number + delta + trend, with an accent personality ──────────
export function StatTile({ label, value, delta, goodDirection, prior, spark, sparkDays, sparkColor,
  sparkLabel = 'last 14 days', kind, currency, to, sampleFlag, help, note }) {
  const hasSpark = spark && spark.filter((v) => v != null).length > 1;
  const anomaly = hasSpark ? endpointAnomaly(spark, goodDirection) : null;
  const momentum = hasSpark ? sevenOverSeven(spark, kind) : null;
  const body = (
    <Card className={cn('g-canvas-tile g-stat group relative h-full overflow-hidden transition-colors',
      to && 'cursor-pointer hover:bg-[hsl(var(--surface-subtle))]')}>
      <CardContent className="px-4 py-3.5">
        <div className="flex items-start justify-between gap-2">
          <p className="flex items-center gap-1 text-[13px] font-medium first-letter:uppercase text-muted-foreground">
            {label}
            {sampleFlag && <FlaskConical className="size-3 text-amber-500" title="uses sample ad spend" />}
            {to && <ArrowUpRight className="size-3 text-primary opacity-0 transition-opacity group-hover:opacity-100" />}
          </p>
          {delta != null && <Delta pct={delta} goodDirection={goodDirection} />}
        </div>
        <p className="mt-2 text-[24px] font-semibold leading-none tabular-nums tracking-[-0.02em] text-foreground"><CountUp value={value} /></p>
        {/* A prior of zero is almost never a real comparison - it means the
            window before this one has no data yet, which is the normal state
            for a store in its first weeks. Printing "prior 0" on every tile
            implied growth from nothing and cost a line of vertical space
            twelve times over. Shown only when there is something to compare
            against. */}
        {prior != null && /[1-9]/.test(String(prior))
          && <p className="mt-1 text-[14px] text-muted-foreground">prior {prior}</p>}
        {/* A plain-language line for a tile that carries no trend: says what the
            number sits beside, on the SAME period as the number itself. */}
        {note && <p className="mt-1.5 text-[13px] leading-snug text-muted-foreground">{note}</p>}
        {/* The trend earns real height: the shape below the number is where
            "is this moving the right way" gets answered without a click. */}
        {hasSpark && (
          <>
            <TrendSpark data={spark} days={sparkDays} kind={kind} currency={currency}
              color={sparkColor || '#2D63D8'} goodDirection={goodDirection} anomaly={anomaly} />
            <div className="mt-1 flex items-center justify-between gap-2">
              <p className="text-[13px] font-medium first-letter:uppercase text-muted-foreground">{sparkLabel}</p>
              {momentum != null && momentum !== 0 && (
                <span title="last 7 days vs the 7 before"
                  className={cn('inline-flex items-center gap-px text-[12px] font-semibold tabular-nums',
                    (goodDirection === 'up' ? momentum > 0 : momentum < 0)
                      ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
                  {momentum > 0 ? <ArrowUpRight className="size-2.5" /> : <ArrowDownRight className="size-2.5" />}
                  7d {momentum > 0 ? '+' : ''}{momentum}%
                </span>
              )}
            </div>
            {anomaly && (
              <p className={cn('mt-1 text-[13px] font-medium leading-snug',
                anomaly.good ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
                yesterday {anomaly.pct}% {anomaly.above ? 'above' : 'below'} the 14-day average
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} title={help} className="g-stat-wrap block h-full">{body}</Link> : <span title={help} className="g-stat-wrap block h-full">{body}</span>;
}

// ── GBar: THE underbar ─────────────────────────────────────────────────────
// One bar language product-wide: a 4px rounded track (border/60) with a fill
// whose color is SEMANTIC, never decorative -
//   brand   honey  = share / magnitude ("how much of the total is this row")
//   good    green  = people or money kept, positive movement
//   bad     red    = people or money lost, negative movement
//   neutral        = context values that carry no judgment
// `color` overrides the tone for categorical series identity (a channel's
// ramp color). Anything drawing its own track div is a remnant to convert.
export function GBar({ pct, tone = 'brand', color, className, trackClass }) {
  const w = `${Math.max(2, Math.min(100, Math.round(pct || 0)))}%`;
  const tones = {
    brand: 'bg-chart-1',
    good: 'bg-emerald-500/85',
    bad: 'bg-rose-500/75',
    neutral: 'bg-chart-1/45',
  };
  return (
    <span className={cn('block h-[4px] w-full overflow-hidden rounded-full bg-border/60', trackClass, className)}>
      <span className={cn('block h-full rounded-full', !color && tones[tone])}
        style={{ width: w, ...(color ? { background: color } : null) }} />
    </span>
  );
}

export function BarCell({ value, max, children, tone = 'brand', color }) {
  const pct = max > 0 ? (100 * (value || 0)) / max : 0;
  return (
    // The bar sits BESIDE the figure, on its line. Under the figure it made
    // every row that carried one a line and a half tall.
    <span className="inline-flex items-center justify-end gap-2 whitespace-nowrap">
      <GBar pct={pct} tone={tone} color={color} className="w-12 shrink-0" />
      <span className="tabular-nums">{children}</span>
    </span>
  );
}

// ── GBarsLine: daily bars with a verdict line over them ────────────────────
// Brandstack's "Net Sales vs Net Margin" shape: quiet bars carry the gross,
// one colored line carries the judgment. The tooltip itemises all series -
// including any that are context rather than ink on the chart.
function BarsLineTip({ active, payload, label, currency, series, fmtFor }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="min-w-[170px] rounded-lg border border-border bg-popover px-3 py-2 text-[13px] shadow-md">
      <div className="mb-1 font-semibold text-foreground">{fmtChartDay(label)}</div>
      {series.map((sr) => (
        <div key={sr.key} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="size-2 rounded-full" style={{ background: sr.color }} />{sr.label}
          </span>
          <span className={cn('tabular-nums font-medium', sr.strong ? 'font-semibold text-foreground' : 'text-foreground')}>
            {fmtFor(sr, row[sr.key], currency)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function GBarsLine({ rows, currency, barKey = 'revenue', lineKey = 'kept',
  barLabel = 'Revenue', lineLabel = 'Kept after ads', extra = [], height = 300, lineAxis = 'left',
  // Not every series on this chart is money. Sessions are a count and a
  // conversion rate is a percentage, and formatting them through moneyShort
  // put a rupee sign in front of both - "Conversion rate % ₹1.61", which is
  // not a small cosmetic slip: it makes the reader distrust every other
  // number on the page. Both default to currency, so every existing chart is
  // unchanged.
  barFmt = null, lineFmt = null, lineTotal = 'avg' }) {
  // The money instrument (approved chart language): the lead series is a
  // smooth AREA fading to nothing under a crisp stroke, the judgment series
  // a clean line over it, everything mounted on the canvas panel. No blur,
  // no ghost series - depth is gradient only.
  const T = useChartTheme();
  const AREA = T.blue, LINE = T.amber;
  const series = [
    { key: barKey, label: barLabel, color: AREA },
    ...extra.map((e, i) => ({ ...e, color: e.color || [T.blue, T.magenta, T.violet][i % 3] })),
    { key: lineKey, label: lineLabel, color: T.amber, strong: true },
  ];
  // "kept after ads"-style verdict lines read green; secondary measures on
  // their own axis (AOV) read cyan. Same rule as the spec mock.
  const lineColor = lineAxis === 'right' ? T.amber : LINE;
  series[series.length - 1].color = lineColor;
  if (!rows?.length) return <p className="py-6 text-center text-xs text-muted-foreground">No data in this window yet.</p>;
  const gradId = `gbl-${barKey}-${lineKey}`;
  const fmtFor = (sr, v, cur) => {
    if (sr.key === barKey && barFmt) return barFmt(v);
    if (sr.key === lineKey && lineFmt) return lineFmt(v);
    return moneyShort(v, cur);
  };
  const totals = series.map((sr) => rows.reduce((a, r) => a + (Number(r[sr.key]) || 0), 0));
  // ABOVE THE PLOT, not under it. The chips carry the series names, their
  // colours and their totals, so they are the key to everything drawn below -
  // and they sat AFTER it, so a reader met two unnamed curves and had to reach
  // the bottom of the card to learn which was which. Reading order should
  // follow dependency order.
  const legend = (
    <div className="mb-2.5 px-1">
      <GChipLegend items={series.map((sr, i) => ({
        label: sr.label, color: sr.color,
        // A rate summed across days is meaningless; the legend shows its
        // average instead, and says so in the label the caller passes.
        value: sr.key === lineKey && (lineAxis === 'right' || lineTotal === 'avg')
          ? fmtFor(sr, totals[i] / rows.length, currency)
          : fmtFor(sr, totals[i], currency),
      }))} />
    </div>
  );
  const grid = <CartesianGrid stroke={T.grid} vertical={false} />;
  const defs = (
    <defs>
      <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={AREA} stopOpacity={T.areaTop} />
        <stop offset=".7" stopColor={AREA} stopOpacity=".04" />
        <stop offset="1" stopColor={AREA} stopOpacity="0" />
      </linearGradient>
    </defs>
  );
  const xTick = { fontSize: 11.5, fill: T.lbl };
  const tip = <ReTooltip content={<BarsLineTip currency={currency} series={series} fmtFor={fmtFor} />} cursor={{ stroke: T.crosshair, strokeWidth: 1 }} />;
  // ONE chart, two scales: bars for the lead series on the LEFT axis, a line
  // for the second on the RIGHT, each axis wearing its series' colour so which
  // scale belongs to which mark is never a guess.
  //
  // This was two stacked panels sharing a syncId, which was itself a fix for an
  // earlier arrangement where the second series got a third of the canvas and
  // read as a footnote. Two panels solved that and cost something else: you
  // could no longer see, in one glance, that a day's revenue spike came with a
  // LOWER order value. The combination is the whole point of drawing them
  // together, and a line laid over bars at full height gives the second series
  // more presence than a squeezed lower pane ever did.
  if (lineAxis === 'right') {
    // Ticks on round numbers the eye can interpolate between. Recharts' own
    // nice-numbering lands on 2 and 4 for a 0-4.5% range; five intervals and
    // a ceiling rounded to a clean step give 1%, 2%, 3%, 4%, 5%.
    const lineMax = Math.max(...rows.map((r) => Number(r[lineKey]) || 0), 0);
    const step = lineMax <= 0 ? 1 : Math.pow(10, Math.floor(Math.log10(lineMax / 5))) * ([1, 2, 2.5, 5, 10].find((m) => lineMax / 5 <= Math.pow(10, Math.floor(Math.log10(lineMax / 5))) * m) || 10);
    const lineCeil = Math.max(step, Math.ceil(lineMax / step) * step);
    return (
      <div>
        {legend}
        <GChartCanvas>
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={rows} margin={{ top: 8, right: 2, left: -4, bottom: 0 }}>
              {defs}{grid}
              <XAxis dataKey="day" tickFormatter={fmtChartDay} tick={xTick} minTickGap={28} axisLine={false} tickLine={false} />
              <YAxis yAxisId="bar" orientation="left" width={56} tickCount={6} axisLine={false} tickLine={false}
                tickFormatter={(v) => fmtFor({ key: barKey }, v, currency)} tick={{ fontSize: 11.5, fill: AREA }} />
              <YAxis yAxisId="line" orientation="right" width={56} axisLine={false} tickLine={false}
                domain={[0, lineCeil]} ticks={Array.from({ length: Math.round(lineCeil / step) + 1 }, (_, i) => i * step)}
                interval={0} minTickGap={0} allowDecimals
                tickFormatter={(v) => fmtFor({ key: lineKey }, v, currency)} tick={{ fontSize: 11.5, fill: lineColor }} />
              {tip}
              {/* Both series are lines. The lead one keeps the soft gradient
                  under its stroke: on a two-scale chart the fill is what says
                  "this one reads against the LEFT axis" without a reader having
                  to match colours to tick labels. */}
              {/* v3 (7 Oct 2026): a day is a bar, and the second series runs
                  straight from day to day. The smoothed area invented peaks
                  and dips between days that the data never had. */}
              <Bar yAxisId="bar" dataKey={barKey} fill={AREA} fillOpacity={0.28} radius={[2, 2, 0, 0]} maxBarSize={18}
                activeBar={{ fillOpacity: 0.5 }} />
              {extra.map((e, i) => (
                <Line key={e.key} yAxisId="bar" dataKey={e.key} stroke={series[1 + i].color} strokeWidth={1.8} dot={false} type="linear" connectNulls />
              ))}
              <Line yAxisId="line" dataKey={lineKey} stroke={lineColor} strokeWidth={2} dot={{ r: 2, fill: lineColor, strokeWidth: 0 }} type="linear" connectNulls
                activeDot={{ r: 4.5, fill: lineColor, stroke: T.dotRing, strokeWidth: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </GChartCanvas>
      </div>
    );
  }
  return (
    <div>
      {legend}
      <GChartCanvas>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={rows} margin={{ top: 6, right: 6, left: -4, bottom: 0 }}>
            {defs}{grid}
            <XAxis dataKey="day" tickFormatter={fmtChartDay} tick={xTick} minTickGap={28} axisLine={false} tickLine={false} />
            <YAxis yAxisId="bar" tickFormatter={(v) => moneyShort(v, currency)} tick={xTick} width={54} tickCount={5} axisLine={false} tickLine={false} />
            {tip}
            <Area yAxisId="bar" dataKey={barKey} type="monotone" stroke={AREA} strokeWidth={2.4} fill={`url(#${gradId})`} dot={false}
              activeDot={{ r: 4.5, fill: AREA, stroke: T.dotRing, strokeWidth: 2 }} />
            {extra.map((e, i) => (
              <Line key={e.key} yAxisId="bar" dataKey={e.key} stroke={series[1 + i].color} strokeWidth={1.8} dot={false} type="monotone" connectNulls />
            ))}
            <Line yAxisId="bar" dataKey={lineKey} stroke={lineColor} strokeWidth={2.2} dot={false} type="monotone" connectNulls
              activeDot={{ r: 4, fill: lineColor, stroke: T.dotRing, strokeWidth: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </GChartCanvas>
    </div>
  );
}

// ── GJourney: "from visitor to money kept", told as an animated story ──────
// Redesigned to explain itself: every stage and every ledger cell carries the
// exact arithmetic behind its number in an InfoTip, the conversion between
// stages sits ON the connector as a pill, and a volume ribbon under the flow
// shows the funnel collapsing (sqrt-scaled, or the order bar would be
// invisible next to sixty thousand sessions - the tooltip says so). Numbers
// count up unless the viewer asked for reduced motion.
function useCountUp(target, ms = 850) {
  const [v, setV] = useState(0);
  React.useEffect(() => {
    const t = Number(target) || 0;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setV(t); return undefined; }
    let raf; const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / ms);
      setV(t * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

const J_ICONS = { visitors: Eye, viewed: PackageSearch, carted: ShoppingCart, checkout: CreditCard, placed: ReceiptText, kept: TrendingUp };

// What each stage MEANS, in the reader's own words. Rendered as the node's
// InfoTip so nobody has to ask what the number is made of.
const J_STAGE_TIPS = {
  visitors: 'Unique browsing sessions on the store in the window. One person on two devices counts twice; one person returning in the same session counts once.',
  viewed: 'Sessions that opened at least one product page (a view_item event). The ratio reads against visitors.',
  carted: 'Sessions that added something to the cart. The ratio reads against product viewers - the weak link in most funnels lives exactly here.',
  checkout: 'Sessions that began checkout. Ratio against carts.',
  placed: 'Every order placed in the window, with its gross value. Counts and money both shown.',
  kept: 'Gross sales minus cancellations, full refunds and partial refunds - the money that actually stayed.',
};

function JNumber({ value, money, currency }) {
  const v = useCountUp(value);
  return <>{money ? moneyShort(v, currency) : Math.round(v).toLocaleString('en-IN')}</>;
}

function JNode({ stage, i, currency }) {
  const Icon = J_ICONS[stage.key] || TrendingUp;
  return (
    <div className="flex w-[116px] shrink-0 flex-col items-center text-center"
      style={{ animation: 'jnode .45s ease-out both', animationDelay: `${i * 0.38}s` }}>
      <div className={cn('relative grid size-14 place-items-center rounded-full border-2 shadow-sm',
        stage.good ? 'card-good text-emerald-600 dark:text-emerald-400'
          : 'border-border bg-card text-foreground')}>
        <Icon className="size-5" />
        <span className={cn('absolute -left-1 -top-1 grid size-5 place-items-center rounded-full text-[13px] font-bold',
          stage.good ? 'bg-emerald-500 text-white' : 'bg-foreground text-background')}>{i + 1}</span>
      </div>
      <p className="mt-2 flex items-center gap-1 text-[14px] font-medium text-muted-foreground">
        {stage.label}
        {J_STAGE_TIPS[stage.key] && <InfoTip size={3}>{J_STAGE_TIPS[stage.key]}</InfoTip>}
      </p>
      <p className={cn('text-[16px] font-bold tabular-nums tracking-tight', stage.good && 'text-emerald-600 dark:text-emerald-400')}>
        <JNumber value={stage.value != null ? stage.value : stage.n} money={stage.value != null} currency={currency} />
      </p>
      {stage.value != null && stage.key !== 'kept' && (
        <p className="text-[13px] tabular-nums text-muted-foreground">{stage.n.toLocaleString('en-IN')} orders</p>
      )}
      {stage.caption && (stage.key === 'kept' || (stage.key === 'checkout' && !/%/.test(stage.caption))) && <p className="text-[13px] text-muted-foreground">{stage.caption}</p>}
    </div>
  );
}

// The funnel's shape, drawn once: height per stage = sqrt(share of visitors).
function JRibbon({ stages }) {
  const counts = (stages || []).filter((st) => st.n != null).map((st) => st.n);
  if (counts.length < 3 || !(counts[0] > 0)) return null;
  const H = 36; const n = counts.length;
  const pts = counts.map((c, i) => ({ x: (100 * i) / (n - 1), h: Math.max(2, H * Math.sqrt(c / counts[0])) }));
  const pathTop = pts.map((p) => `${p.x},${H - p.h}`).join(' L');
  const polyTop = pts.map((p) => `${p.x},${H - p.h}`).join(' ');
  return (
    <div title="The funnel's shape: each stage's height is the square root of its share of visitors - drawn that way so the order stage stays visible beside sixty thousand sessions."
      style={{ animation: 'jfade .6s ease-out both', animationDelay: `${(n + 1) * 0.38}s` }}>
      <svg viewBox={`0 0 100 ${H}`} preserveAspectRatio="none" className="h-9 w-full" aria-hidden>
        <path d={`M0,${H} L${pathTop} L100,${H} Z`} fill="#2D63D8" opacity="0.12" />
        <polyline points={polyTop} fill="none" stroke="#2D63D8" strokeWidth="1" opacity="0.5" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

export function GJourney({ trigger }) {
  const [open, setOpen] = useState(false);
  const [run, setRun] = useState(0);
  const [d, setD] = useState(null);
  const load = () => api.get('/api/commerce/growth/journey').then((r) => setD(r.data)).catch(() => setD({ error: true }));
  const onOpen = (v) => { setOpen(v); if (v && !d) load(); if (v) setRun((r) => r + 1); };
  const c = d?.currency;
  const lost = (d?.deductions || []).filter((x) => x.value > 0);
  const lostN = (d?.deductions || []).reduce((a, x) => a + (x.n || 0), 0);
  const lostV = (d?.deductions || []).reduce((a, x) => a + (x.value || 0), 0);
  const sm = d?.summary || {};
  // The ledger: value, caption, and the exact arithmetic in a tip.
  const ledger = [
    { label: 'Avg order value', v: sm.aov != null ? moneyShort(sm.aov, c) : '-',
      cap: 'per kept order',
      tip: 'Money kept ÷ orders kept. Cancelled and refunded orders are out of both sides.' },
    { label: 'AOV · paid channels', v: sm.aov_paid != null ? moneyShort(sm.aov_paid, c) : '-',
      cap: 'first touch was a paid ad',
      tip: 'Average order value counting only orders whose FIRST touch was a paid channel (Meta Ads, Google Ads, other paid). Compare with the blended AOV to see whether ad-bought baskets run smaller.' },
    { label: 'New customers won', v: (sm.new_customers ?? 0).toLocaleString('en-IN'),
      cap: 'first-ever purchase',
      tip: 'People whose first purchase with the store happened in this window - buyers the store already had (per their customer record\u2019s creation date) are excluded, same rule as the Customer Value table.' },
    { label: 'Returning buyers', v: sm.returning_buyers != null
        ? `${sm.returning_buyers.toLocaleString('en-IN')}${sm.returning_pct != null ? ` · ${sm.returning_pct}%` : ''}` : '-',
      cap: sm.buyers ? `of ${sm.buyers.toLocaleString('en-IN')} buyers, had ordered before` : null,
      tip: 'Buyers this window who had ordered before, here or in the store\u2019s own history: the same returning rule as every report. A new customer who came back inside the window counts here and under New customers won.' },
    { label: 'Top channel', v: sm.top_channel ? `${sm.top_channel.channel} (${sm.top_channel.share_pct}%)` : '-',
      cap: 'share of gross sales',
      tip: 'The first-touch channel carrying the largest share of gross sales in the window - paid and unpaid alike.' },
    { label: 'Top paid channel', v: sm.top_paid_channel ? `${sm.top_paid_channel.channel} (${sm.top_paid_channel.share_pct}%)` : '-',
      cap: 'share of gross sales',
      tip: 'The largest PAID first-touch channel by gross sales - the one your ad budget should be judged against.' },
    { label: 'Returned / cancelled', v: lostN > 0 ? `${lostN} orders · −${moneyShort(lostV, c)}` : 'none',
      cap: lostN > 0 ? 'includes partial refunds' : 'nothing lost this window',
      tip: 'Cancelled orders, fully refunded orders, and the partial amounts paid orders gave back. Requires the orders/updated and orders/cancelled webhooks to be registered on the store.' },
    { label: 'Revenue kept', v: sm.kept_pct != null ? `${sm.kept_pct}%` : '-',
      cap: 'of gross sales survived',
      tip: 'Money kept ÷ gross sales. 100% means nothing was cancelled or refunded in this window.' },
  ];
  return (
    <>
      <button type="button" onClick={() => onOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--card-border))] bg-card px-3 py-1.5 text-xs font-medium shadow-sm transition-colors hover:bg-muted">
        <Waypoints className="size-3.5" />{trigger || 'Money Journey'}<Play className="size-3 fill-current" />
      </button>
      <Dialog open={open} onOpenChange={onOpen}>
        <DialogContent className="max-w-5xl gap-0 overflow-hidden p-0">
          <style>{`
            @keyframes jnode { from { opacity: 0; transform: translateY(10px) scale(.95); } to { opacity: 1; transform: none; } }
            @keyframes jarrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
            @keyframes jfade { from { opacity: 0; } to { opacity: 1; } }
          `}</style>
          <div className="flex items-start justify-between border-b border-border bg-muted/30 px-6 py-4">
            <div>
              <p className="flex items-center gap-1.5 text-[13px] font-bold first-letter:uppercase text-primary">
                <Waypoints className="size-3" /> Money Journey
                <span className="ml-1 rounded-full border border-[hsl(var(--card-border))] bg-card px-2 py-0.5 font-semibold normal-case tracking-normal text-muted-foreground">last {d?.window_days || 30} days</span>
              </p>
              <DialogTitle className="mt-1 text-lg font-semibold tracking-tight">From visitor to money kept</DialogTitle>
              <DialogDescription className="mt-0.5 text-[13px] text-muted-foreground">
                How traffic became revenue, and what survived. Revenue before cost of goods - hover any (i) for the exact arithmetic.
              </DialogDescription>
            </div>
            <button type="button" onClick={() => setRun((r) => r + 1)}
              className="mr-8 inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--card-border))] bg-card px-2.5 py-1.5 text-[14px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
              <RotateCcw className="size-3" /> Replay
            </button>
          </div>
          {!d && <div className="grid place-items-center py-16 text-xs text-muted-foreground">Loading…</div>}
          {d?.error && <div className="grid place-items-center py-16 text-xs text-muted-foreground">Could not load the journey.</div>}
          {d && !d.error && (
            <div key={run}>
              <div className="overflow-x-auto px-6 pb-2 pt-8">
                <div className="min-w-[820px]">
                  <div className="flex items-start">
                    {(d.stages || []).map((st, i) => {
                      const ratio = st.caption && /%/.test(st.caption) ? st.caption.split(' ')[0] : null;
                      return (
                        <React.Fragment key={st.key}>
                          {i > 0 && (
                            <div className="relative mt-7 flex-1" style={{ minWidth: 30 }}>
                              {i === d.stages.length - 1 && (
                                <div className="absolute -top-6 left-0 right-0 flex justify-center gap-1.5" style={{ animation: 'jfade .4s ease-out both', animationDelay: `${i * 0.38}s` }}>
                                  {/* One tag on the connector, the split on hover and in the
                                      Returned / cancelled cell below. Three tags (cancelled,
                                      refunded, partial) did not fit the gap and ran over the
                                      Orders placed circle and off the dialog (8 Oct 2026). */}
                                  {lost.length ? (
                                    <span title={lost.map((x) => `${x.label} −${moneyShort(x.value, c)}`).join(' · ')}
                                      className="cursor-help whitespace-nowrap rounded-md border card-critical px-1.5 py-0.5 text-[13px] font-semibold tabular-nums text-rose-700 dark:text-rose-400">
                                      −{moneyShort(lostV, c)}
                                    </span>
                                  ) : (
                                    <span className="whitespace-nowrap rounded-md border card-good px-1.5 py-0.5 text-[13px] font-semibold text-emerald-700 dark:text-emerald-400">
                                      nothing cancelled or refunded
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className={cn('h-[2px] origin-left rounded-full', i === d.stages.length - 1 ? 'bg-emerald-500' : 'bg-border')}
                                style={{ animation: 'jarrow .3s ease-out both', animationDelay: `${(i - 1) * 0.38 + 0.22}s` }} />
                              {ratio && i !== d.stages.length - 1 && (
                                <span title={d.stages[i].caption}
                                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-help rounded-full border border-[hsl(var(--card-border))] bg-card px-2 py-0.5 text-[14px] font-semibold tabular-nums text-muted-foreground shadow-sm"
                                  style={{ animation: 'jfade .3s ease-out both', animationDelay: `${(i - 1) * 0.38 + 0.3}s` }}>
                                  {ratio}
                                </span>
                              )}
                            </div>
                          )}
                          <JNode stage={st} i={i} currency={c} />
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <JRibbon stages={d.stages} />
                </div>
              </div>
              <div className="grid grid-cols-2 border-t border-border sm:grid-cols-4"
                style={{ animation: 'jfade .5s ease-out both', animationDelay: `${((d.stages?.length || 6) - 1) * 0.38 + 0.4}s` }}>
                {ledger.map((x, i) => (
                  <div key={x.label} className={cn('px-4 py-3', i % 4 !== 0 && 'border-l border-border', i >= 4 && 'border-t border-border')}>
                    <p className="flex items-center gap-1 text-[13px] font-bold first-letter:uppercase text-muted-foreground">
                      {x.label}
                      {x.tip && <InfoTip size={3}>{x.tip}</InfoTip>}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold tabular-nums">{x.v}</p>
                    {x.cap && <p className="text-[13px] text-muted-foreground">{x.cap}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── GStatTile: Brandstack's scorecard - label, value, chip, caption, spark ──
// The sparkline is an area-backed polyline in the tile's own footer, drawn in
// whatever color class the caller passes - direction is the CALLER's verdict,
// not something a squiggle should decide for itself.
function TileSpark({ values, className }) {
  const gid = React.useId();
  if (!values || values.length < 2) return null;
  const max = Math.max(...values); const min = Math.min(...values);
  const span = max - min || 1;
  const xy = values.map((v, i) => [(100 * i) / (values.length - 1), 26 - ((v - min) / span) * 22]);
  const d = smoothPath(xy);
  const last = xy[xy.length - 1];
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none" className={cn('h-7 w-full overflow-visible', className)} aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity=".2" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L100,28 L0,28 Z`} fill={`url(#${gid})`} />
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.2" fill="currentColor" />
    </svg>
  );
}

export function GStatRow({ children, className }) {
  return <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5', className)}>{children}</div>;
}

// `bar`: optional stacked split drawn under the value, [{ pct, cls, label }];
// the Orders page's Attributed tile carries its channel split this way rather
// than in a caption that ran off the card.
// `compact`: a summary strip above a table, not the page's headline numbers.
// Title, value and its caption at a smaller step and tighter padding, so four
// tiles cost ~90px instead of ~140 and the table the page is for starts on
// screen. The caption is never cut: a sentence ending in '...' is unfinished.
export function GStatTile({ icon: Icon, label, tip, value, chip, chipTone, caption, spark, sparkClass = 'text-muted-foreground', bar, compact = false }) {
  if (compact) {
    return (
      <Card className="bs-card g-canvas-tile g-stat"><CardContent className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
          {Icon && <Icon className="size-3.5 shrink-0" />}
          <span className="truncate">{label}</span>
          {tip && <span className="cursor-help"><InfoTip size={3}>{tip}</InfoTip></span>}
        </div>
        <p className="mt-1 text-[20px] font-semibold leading-tight tabular-nums tracking-[-0.02em] text-foreground"><CountUp value={value} /></p>
        {caption && <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-muted-foreground" title={caption}>{caption}</p>}
      </CardContent></Card>
    );
  }
  return (
    // bs-card as well as the canvas tile: the KPI row was the one set of cards
    // in the product that did not darken its border on hover, so the top of
    // every page felt subtly dead next to everything below it.
    <Card className="bs-card g-canvas-tile g-stat"><CardContent className={cn('px-4 pt-3.5', spark?.length >= 2 ? 'pb-2' : 'pb-3.5')}>
      {/* Sentence case, the page's own label size. The spaced-out capitals
          this replaced were the loudest thing on every tile and the first
          thing that made a page read as generated. */}
      <div className="flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground">
        {Icon && <Icon className="size-3.5 shrink-0" />}
        <span className="truncate">{label}</span>
        {tip && <span className="cursor-help"><InfoTip size={3}>{tip}</InfoTip></span>}
      </div>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[24px] font-semibold leading-none tabular-nums tracking-[-0.02em] text-foreground"><CountUp value={value} /></span>
        {chip != null && (
          // Never wraps inside itself: short of room, the whole chip drops
          // under the number (the rule for any caption beside a big figure).
          <span className={cn('whitespace-nowrap text-[13px] font-semibold tabular-nums',
            chipTone === 'good' ? 'text-emerald-700 dark:text-emerald-400'
              : chipTone === 'bad' ? 'text-rose-600 dark:text-rose-400'
                : chipTone === 'warn' ? 'text-amber-800 dark:text-amber-300'
                : 'text-muted-foreground')}>{chip}</span>
        )}
      </div>
      {bar?.length > 0 && (
        <div className="mt-2.5 flex h-1.5 w-full gap-0.5 overflow-hidden rounded-full bg-muted" aria-hidden>
          {bar.filter((b) => b.pct > 0).map((b) => <span key={b.label} className={cn('h-full rounded-full', b.cls)} style={{ width: `${b.pct}%` }} title={`${b.label} ${Math.round(b.pct)}%`} />)}
        </div>
      )}
      {/* Two lines, never a cut: a caption that ends in an ellipsis was the
          one thing on the tile a reader could not finish. Rows stay even
          because the grid stretches every card to the tallest. */}
      {caption && <p className="mt-1.5 line-clamp-2 text-[13px] leading-snug text-muted-foreground" title={caption}>{caption}</p>}
      {spark?.length >= 2 && <TileSpark values={spark} className={cn('mt-1.5', sparkClass)} />}
    </CardContent></Card>
  );
}

// ── GSegment: the small segmented toggle Brandstack puts on cards ──────────
// One control, two-to-four views of the SAME thing. Not a filter: switching
// never changes which rows exist, only which measure of them is drawn.
// Brandstack's status pill, measured live: 10px/600, colored text on its own
// soft color at 14%, radius 8, padding 2x8, no border, no uppercase. tone:
// good | warn | bad | calm. One pill for every verdict in the product.
// `dot`: a leading dot in the pill's own colour, for a pill that reports a
// STATE (Live, Not linked, Cannot send yet) rather than a value, so a status
// never reads as a button or a tag.
export function GPill({ tone = 'calm', children, className, title, dot }) {
  const t = {
    good: 'bg-emerald-600/[0.14] text-emerald-600 dark:text-emerald-400',
    warn: 'bg-amber-600/[0.14] text-amber-600 dark:text-amber-400',
    bad: 'bg-rose-600/[0.12] text-rose-600 dark:text-rose-400',
    calm: 'bg-muted text-muted-foreground',
    info: 'bg-sky-600/[0.12] text-sky-700 dark:text-sky-400',
  }[tone] || 'bg-muted text-muted-foreground';
  return (
    <span title={title} className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-[8px] px-2 py-0.5 text-[13px] font-semibold', dot && 'gap-1.5', t, className)}>
      {dot && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />}
      {children}
    </span>
  );
}

// ── NameCell: a row's name with its kind tag ───────────────────────────────
// ONE rule for every "name + tag" cell: the name on its own line (never
// wrapped), the tag on the line under it, always. Inline, the tag sat beside
// a short name and dropped under a long one, so down one column it jumped
// between two places and the rows changed height with it.
export function NameCell({ name, tag, tone = 'calm', tagTitle, mono = false, className }) {
  return (
    <span className={cn('flex min-w-0 flex-col items-start gap-1', className)}>
      <span className={cn('whitespace-nowrap font-semibold text-foreground', mono && 'font-mono text-[13px]')}>{name}</span>
      {tag && <GPill tone={tone} title={tagTitle} className="px-1.5 py-0 text-[12px] font-medium leading-[18px]">{tag}</GPill>}
    </span>
  );
}

export function GSegment({ value, onChange, options }) {
  // A segmented control that reads as one: a recessed track, and the chosen
  // option raised out of it as a white key (owner, 8 Oct 2026: the flat
  // grey-on-hover words "appear like some text and not buttons"). Every
  // option keeps its own hover and focus ring, so each looks pressable.
  return (
    <div role="group" className="inline-flex shrink-0 items-center gap-0.5 rounded-lg border border-[hsl(var(--card-border))] bg-muted/70 p-0.5">
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} aria-pressed={on} title={o.title}
            className={cn('h-7 whitespace-nowrap rounded-md px-3 text-[13.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              on ? 'bg-card font-semibold text-foreground shadow-sm ring-1 ring-[hsl(var(--card-border))]'
                : 'font-medium text-muted-foreground hover:bg-card/70 hover:text-foreground')}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── GStackedBars: a day-by-day stacked bar, one color per category ─────────
// Brandstack's "Sales by Channel" shape: every day is one bar, each channel a
// segment, a tooltip that itemises the day and totals it, and a dot legend.
// Categories beyond maxCats fold into "Other" - a chart with eleven colors is
// a legend with a chart attached. Colors are assigned by RANK ONCE, from the
// window's totals, so a channel keeps its color across days.
// Brandstack's categorical ramp, verbatim (their --c1..--c5 + purple, then a
// warm neutral for "Other"). Gold leads, teal second - color follows entity.
// Chart series palette (user call, 28 Aug): the full 8-color
// learnui.design data ramp, exactly as the user exported it, INTERLEAVED
// so stacked neighbours stay distinct (validated: worst adjacent CVD
// dE 8.6, normal 17.7 - legal with our gaps + legends + tooltips).
// 'Other' never wears a series color - it is special-cased to gray.
export const STACK_COLORS = ['#2D63D8', '#7FB0F5', '#765BC7', '#EDBD48', '#E9766D', '#8FA8A0'];
export const OTHER_COLOR = '#8FA8A0';

function StackTip({ active, payload, label, show = (v) => v.toLocaleString('en-IN') }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value > 0);
  const total = rows.reduce((a, p) => a + p.value, 0);
  return (
    <div className="min-w-[150px] rounded-lg border border-border bg-popover px-3 py-2 text-[13px] shadow-md">
      <div className="mb-1 font-semibold text-foreground">{fmtChartDay(label)}</div>
      {rows.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <span className="size-2 rounded-full" style={{ background: p.fill }} />{p.dataKey}
          </span>
          <span className="tabular-nums font-medium text-foreground">{show(p.value)}</span>
        </div>
      ))}
      <div className="mt-1 flex items-center justify-between gap-4 border-t border-border pt-1 font-semibold text-foreground">
        <span>Total</span><span className="tabular-nums">{show(total)}</span>
      </div>
    </div>
  );
}

const fmtChartDay = (d) => {
  const t = new Date(`${d}T00:00:00`);
  return Number.isNaN(t.getTime()) ? d : t.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Strata treatment: each segment is a vertical gradient with a bright
// cap-line on its top edge and a breath of canvas between layers - the
// stack reads as built, not painted.
function StrataSegment(props) {
  const { x, y, width, height, capColor } = props;
  if (!height || height <= 0 || !width) return null;
  const h = Math.max(1.5, height - 1.5);
  return (
    <g>
      <rect x={x} y={y} width={width} height={h} rx={2} fill={capColor} />
    </g>
  );
}

export function GStackedBars({ rows, catKey = 'source', valKey = 'customers', maxCats = 5, height = 230, fmt, catOrder, yMin = 0 }) {
  const T = useChartTheme();
  const show = fmt || ((v) => v.toLocaleString('en-IN'));
  const { data, cats } = useMemo(() => {
    const totals = {};
    for (const r of rows || []) totals[r[catKey]] = (totals[r[catKey]] || 0) + num2(r[valKey]);
    const byTotal = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
    const ranked = catOrder ? [...catOrder.filter((k) => k in totals), ...byTotal.filter((k) => !catOrder.includes(k))] : byTotal;
    const keep = ranked.slice(0, maxCats);
    const keepSet = new Set(keep);
    const byDay = new Map();
    for (const r of rows || []) {
      const d = byDay.get(r.day) || { day: r.day };
      const cat = keepSet.has(r[catKey]) ? r[catKey] : 'Other';
      d[cat] = (d[cat] || 0) + num2(r[valKey]);
      byDay.set(r.day, d);
    }
    return {
      data: [...byDay.values()].sort((a, b) => (a.day < b.day ? -1 : 1)),
      cats: ranked.length > maxCats ? [...keep, 'Other'] : keep,
    };
  }, [rows, catKey, valKey, maxCats, catOrder]);
  if (!data.length) return <p className="py-6 text-center text-xs text-muted-foreground">No data in this window yet.</p>;
  const colorOf = (cat, i) => (cat === 'Other' ? T.other : (channelColor(cat) || T.series[i % T.series.length]));
  const catTotals = cats.map((cat) => data.reduce((a, d) => a + (d[cat] || 0), 0));
  const minTotal = Math.min(...data.map((d) => cats.reduce((t, c) => t + (d[c] || 0), 0)));
  const floor = yMin > 0 && Number.isFinite(minTotal) && minTotal > 0
    ? Math.min(yMin, Math.floor(minTotal * 0.8)) : 0;
  const trimmed = floor >= yMin * 0.1;
  const uid = `gsb-${catKey}-${valKey}`;
  return (
    <div>
      {/* Above the plot, for the same reason as the line charts: the chips name
          the layers, and a stack of unnamed bands is unreadable until they do.
          The axis note travels with them - it is about the same picture. */}
      <div className="mb-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1">
        <GChipLegend items={cats.map((cat, i) => ({ label: cat, color: colorOf(cat, i), value: show(catTotals[i]) }))} />
        {trimmed && floor > 0 && (
          <span className="text-[13px] text-muted-foreground">axis from {show(floor)}</span>
        )}
      </div>
      <GChartCanvas>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} barCategoryGap="26%" margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
            <defs>
              {cats.map((cat, i) => (
                <linearGradient key={cat} id={`${uid}-${i}`} x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0" stopColor={colorOf(cat, i)} stopOpacity={T.stackBot} />
                  <stop offset="1" stopColor={colorOf(cat, i)} stopOpacity={T.stackTop} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke={T.grid} vertical={false} />
            <XAxis dataKey="day" tickFormatter={fmtChartDay} tick={{ fontSize: 11.5, fill: T.lbl }} minTickGap={28} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11.5, fill: T.lbl }} width={fmt ? 54 : 42} tickFormatter={fmt} allowDecimals={false} axisLine={false} tickLine={false}
              domain={trimmed ? [floor, 'auto'] : [0, 'auto']} allowDataOverflow={trimmed} tickCount={5} />
            <ReTooltip content={<StackTip show={show} />} cursor={{ fill: T.crosshair, opacity: 0.14 }} />
            {cats.map((cat, i) => (
              <Bar key={cat} dataKey={cat} stackId="a" fill={colorOf(cat, i)} maxBarSize={44}
                shape={<StrataSegment capColor={colorOf(cat, i)} />} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </GChartCanvas>
    </div>
  );
}
const num2 = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// ── GTable: sortable, zebra-hover, sticky-header data table ────────────────
// cols: [{ key, label, align, sortable=true, render(row), sortVal(row), width,
//          minWidth, fill }]
//   minWidth: a floor for a numeric column, so its label stays on one line.
//   fill:     this column takes the width the others leave (the name column).
//
// caption: the window or unit every column shares, said ONCE above the table
// ("Last 28 days") instead of in every header ("Sent, 28 days"), which is what
// wrapped the headers onto two lines. headNowrap keeps each label on one line;
// give numeric columns a minWidth and one column `fill` when you set it.
//
// preview: Brandstack's card-and-drawer pattern. A report card is a place to
// READ, and forty campaigns in it bury the ten that matter - so the card
// keeps the top rows and a footer says plainly how many it is sitting on.
// "View all" opens a full-height sheet: same columns, same sort, every row,
// with a search box and a count. The sheet is just this component again with
// preview off, so the two views can never drift apart. Preview declines to
// engage with grouped tables (_group/_total rows) - slicing a group in half
// would show a heading whose rows are missing.
// ── The new reports' table upgrades (reports plan, "Better tables") ───────
// Only while the new reports are on, so every current table stays exactly as
// it is. A column with no definition of its own borrows the one sentence from
// lib/metric-defs, matched on its key or its label, so the same word means the
// same thing on every page and says so on hover.
const DEF_BY_KEY = {
  revenue: 'revenue', orders: 'orders', aov: 'aov', roas: 'roas', poas: 'poas', margin_pct: 'margin', margin: 'margin',
  contribution: 'contribution', spend: 'ad_spend', ad_spend: 'ad_spend', ctr: 'ctr', cpc: 'cpc', sessions: 'sessions',
  units: 'units', refund_rate: 'refund_rate', ltv: 'ltv', cac: 'cac', ncac: 'ncac', cost_per_new_customer: 'ncac',
  conversion_rate: 'conversion_rate', cvr: 'conversion_rate', mer: 'mer', emq: 'emq', match: 'emq',
};
const DEF_BY_LABEL = {
  revenue: 'revenue', orders: 'orders', 'avg order value': 'aov', aov: 'aov', 'order value': 'aov', roas: 'roas', poas: 'poas',
  'gross margin': 'margin', contribution: 'contribution', spend: 'ad_spend', 'ad spend': 'ad_spend', ctr: 'ctr', cpc: 'cpc',
  sessions: 'sessions', units: 'units', 'units sold': 'units', refunded: 'refund_rate', 'refund rate': 'refund_rate',
  'cost / new customer': 'ncac', 'cost per new customer': 'ncac', 'conversion rate': 'conversion_rate', mer: 'mer', match: 'emq',
};
function defFor(c) {
  const k = DEF_BY_KEY[c.key] || DEF_BY_LABEL[String(typeof c.label === 'string' ? c.label : '').trim().toLowerCase()];
  return k ? METRIC_DEFS[k] : null;
}
// A cell as plain text for the CSV: the column's own csv(), else the raw field,
// else its sort value. Never the drawn cell, which is layout, not data.
function csvValue(c, r) {
  if (c.csv) return c.csv(r);
  const raw = r[c.key];
  if (raw != null && typeof raw !== 'object') return raw;
  if (c.sortVal) return c.sortVal(r);
  return r.label || '';
}
function downloadCsv(cols, rows, name) {
  const esc = (v) => { const t = v == null ? '' : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
  const head = cols.map((c) => esc(typeof c.label === 'string' ? c.label : c.key)).join(',');
  const body = rows.filter((r) => !r._group).map((r) => cols.map((c) => esc(csvValue(c, r))).join(',')).join('\n');
  const blob = new Blob([`${head}\n${body}\n`], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${String(name || 'report').replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'report'}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export function GTable({ cols: allCols, rows, initSort, initDir = 'desc', empty = 'No rows in this window.',
  // headNowrap defaults ON: headers never wrap (standing rule). A header that
  // broke onto three lines ("Journeys · touched / closed / alone") made the
  // header row taller than the data and pushed the numbers out of line.
  preview, sheetTitle, sheetChip, searchKeys, searchable, searchPlaceholder, tableId, caption, headNowrap = true,
  // expand(row) -> what opens UNDERNEATH that row when it is clicked, and
  // rowKey(row) -> the id that says which row is open. The standing rule is
  // that a drill-down opens under its row rather than in a dialog, and this
  // was hand-rolled in one page and unavailable to every other table; a chart
  // for one row belongs beside the row, not in a layer over it.
  expand, rowKey,
  // colLines: a line between every column (owner, 3 Nov 2026, on Campaigns:
  // "make the column dividers a little darker"). Wide number tables read
  // across a row more easily with them; short tables do not need them, so
  // it is the table's choice.
  colLines = false,
  // openKey: a row to open and bring into view, from outside (a link from
  // another page names the event whose row it wants: Goals ?event=).
  openKey = null }) {
  // Column chooser. A wide table shows its important columns by default -
  // a column marked `optional: true` starts hidden - and the reader picks the
  // rest. The choice is remembered per table (tableId) so it survives a
  // reload. Sorting and search still work over every column, hidden or not.
  const [openRow, setOpenRow] = useState(null);
  const keyOf = (r, i) => (rowKey ? rowKey(r) : (r.id || r.key || i));
  // THE DRILL-DOWN STAYS IN VIEW while the table scrolls sideways (owner, 3
  // Nov 2026: "the section also moves when I move right to see the columns").
  // It is sticky to the left edge of the scroll box, and a sticky box only
  // stays put while it is narrower than the row it sits in: as wide as the
  // whole table, it scrolled away with it. So it is as wide as the visible
  // part of the table, measured here and kept as the box resizes.
  const scrollRef = useRef(null);
  const [viewW, setViewW] = useState(null);
  const canExpand = !!expand;
  const hasRows = Boolean(rows?.length);
  useEffect(() => {
    if (openKey == null || !canExpand || !hasRows) return undefined;
    setOpenRow(openKey);
    // After the open row has drawn: its own row to the top of the view.
    const t = setTimeout(() => {
      const el = scrollRef.current?.querySelector(`tr[data-row-key="${CSS.escape(String(openKey))}"]`);
      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 60);
    return () => clearTimeout(t);
  }, [openKey, canExpand, hasRows]);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!canExpand || !el) return undefined;
    const measure = () => setViewW(el.clientWidth || null);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [canExpand]);
  // Keyed by tableId: one GTable instance often serves several tabs (the
  // Campaigns report swaps its columns per tab), and a list captured on the
  // first tab left every optional column of the others showing - the Return
  // tab scrolled sideways with all 13.
  const initialHidden = (id) => {
    try { if (id) { const v = JSON.parse(localStorage.getItem(`gt:cols:${id}`) || 'null'); if (Array.isArray(v)) return v; } } catch { /* private window */ }
    return allCols.filter((c) => c.optional).map((c) => c.key);
  };
  const [hiddenBy, setHiddenBy] = useState(() => ({ id: tableId, list: initialHidden(tableId) }));
  const hidden = hiddenBy.id === tableId ? hiddenBy.list : initialHidden(tableId);
  const cols = useMemo(() => allCols.filter((c) => !hidden.includes(c.key)), [allCols, hidden]);
  const toggleCol = (k) => {
    const next = hidden.includes(k) ? hidden.filter((x) => x !== k) : [...hidden, k];
    try { if (tableId) localStorage.setItem(`gt:cols:${tableId}`, JSON.stringify(next)); } catch { /* private window */ }
    setHiddenBy({ id: tableId, list: next });
  };
  // New reports: the chooser on every table (it vanished at five columns or
  // fewer, so a hidden column there could never come back), a definition on
  // every known metric, a CSV of what is on screen, and a long table that
  // scrolls inside itself so its header and its names stay in view.
  const v2 = reportsV2Live();
  const chooser = allCols.length > 5 || (v2 && allCols.length > 2);
  const tipOf = (c) => c.tip || (v2 ? defFor(c) : null);
  // The names stay put when a wide table scrolls sideways, for everyone
  // (owner, 17 Oct 2026: Campaigns could not be read once it scrolled).
  const stickyFirst = allCols.length > 5;
  const hasHelp = allCols.some((c) => c.help);
  const [sort, setSort] = useState(initSort || null);
  const [dir, setDir] = useState(initDir);
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = cols.find((c) => c.key === sort);
    const val = (r) => (col?.sortVal ? col.sortVal(r) : r[sort]);
    const cmp = (a, b) => {
      const av = val(a), bv = val(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return dir === 'asc' ? av - bv : bv - av;
    };
    // Structural rows hold their place. A group heading is a divider, not a
    // value, and a subtotal sorted by size would float to the top of the very
    // rows it summarises. So the list is cut at each structural row and only
    // the data BETWEEN them is reordered - sorting still works, inside the
    // group where it means something.
    if (!rows.some((r) => r._group || r._total)) return [...rows].sort(cmp);
    const out = [];
    let block = [];
    const flush = () => { if (block.length) { out.push(...block.sort(cmp)); block = []; } };
    for (const r of rows) {
      if (r._group || r._total) { flush(); out.push(r); } else block.push(r);
    }
    flush();
    return out;
  }, [rows, sort, dir, cols]);
  const onSort = (k, sortable) => {
    if (sortable === false) return;
    if (sort === k) setDir(dir === 'asc' ? 'desc' : 'asc');
    else { setSort(k); setDir('desc'); }
  };
  const [sheetOpen, setSheetOpen] = useState(false);
  const [q, setQ] = useState('');
  const structural = useMemo(() => rows.some((r) => r._group || r._total), [rows]);
  // Not worth a drawer to reveal one hidden row: only engage when it hides a few.
  const canPreview = !!preview && !structural && sorted.length > preview + 2;
  const shown = canPreview ? sorted.slice(0, preview) : sorted;
  // Card-level search (searchable prop): filters THIS table in place, not just
  // the View-all sheet. A live needle searches the FULL row set (preview cap
  // lifted - hiding matches behind "top N" would make search a liar) and
  // drops group/total band rows, whose subtotals no longer describe what is
  // on screen once rows are filtered out from under them.
  const cardNeedle = searchable ? q.trim().toLowerCase() : '';
  const visible = useMemo(() => {
    if (!cardNeedle) return shown;
    const keys = searchKeys?.length ? searchKeys : [cols[0]?.key].filter(Boolean);
    return sorted.filter((r) => !r._group && !r._total
      && keys.some((k) => String(r[k] ?? '').toLowerCase().includes(cardNeedle)));
  }, [cardNeedle, shown, sorted, searchKeys, cols]);
  const [hoverCol, setHoverCol] = useState(null);
  const sheetRows = useMemo(() => {
    if (!canPreview) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    const keys = searchKeys?.length ? searchKeys : [cols[0]?.key].filter(Boolean);
    return rows.filter((r) => keys.some((k) => String(r[k] ?? '').toLowerCase().includes(needle)));
  }, [canPreview, rows, q, searchKeys, cols]);
  const chooserUi = chooser && (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-card px-2.5 text-[13px] font-medium text-foreground transition-colors hover:border-border-strong dark:text-ink-100">
          <SlidersHorizontal className="size-3.5" /> Columns
          {hidden.length > 0 && <span className="rounded-full bg-muted px-1.5 text-[11px] tabular-nums text-muted-foreground">{allCols.length - hidden.length}/{allCols.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <p className="px-2 pb-1.5 pt-1 text-[13px] font-medium first-letter:uppercase text-muted-foreground">Show columns</p>
        <div className="max-h-72 overflow-y-auto">
          {allCols.map((c, i) => (
            <label key={c.key} className={cn('flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1.5 text-[13.5px] hover:bg-accent', i === 0 && 'opacity-60')}>
              <input type="checkbox" className="size-3.5 accent-[#087A59]" checked={!hidden.includes(c.key)} disabled={i === 0} onChange={() => toggleCol(c.key)} />
              <span className="truncate">{c.label}</span>
            </label>
          ))}
        </div>
        {hidden.length > 0 && (
          <button type="button" onClick={() => { setHiddenBy({ id: tableId, list: [] }); try { if (tableId) localStorage.removeItem(`gt:cols:${tableId}`); } catch { /* private window */ } }}
            className="mt-1 w-full rounded-sm px-2 py-1.5 text-left text-[12.5px] text-primary hover:bg-accent">Show all</button>
        )}
      </PopoverContent>
    </Popover>
  );
  return (
    <div>
      {(searchable || chooser || caption || v2 || hasHelp) && (
        <div className="mb-2 flex items-center justify-end gap-2">
          {caption && <p className="mr-auto text-[13px] text-muted-foreground">{caption}</p>}
          <TableHelp cols={cols} rows={visible} />
          {chooserUi}
          {v2 && sorted.length > 0 && (
            <button type="button" onClick={() => downloadCsv(cols, cardNeedle ? visible : sorted, tableId || sheetTitle)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-card px-2.5 text-[13px] font-medium text-foreground transition-colors hover:border-border-strong dark:text-ink-100"
              title="Download these rows as a spreadsheet (CSV)">
              <Download className="size-3.5" /> CSV
            </button>
          )}
          {searchable && (<>
          {cardNeedle && (
            <span className="text-[14px] tabular-nums text-muted-foreground">
              {visible.length} match{visible.length === 1 ? '' : 'es'}
            </span>
          )}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder || 'Search by name...'}
              className="h-8 w-56 rounded-md border border-input bg-card pl-7 pr-2 text-[14px] text-foreground outline-none placeholder:text-muted-foreground hover:border-border-strong focus:border-primary" />
          </div>
          </>)}
        </div>
      )}
      {/* A long table scrolls inside itself so its header stays in view
          (owner, 17 Oct 2026: on Campaigns the column names scrolled off the
          top and the numbers under them could not be read). Tables whose rows
          open a drill-down too: Campaigns is one, and was left out. */}
      <div ref={scrollRef} className={cn('overflow-x-auto rounded-xl border border-border', visible.length > 15 && 'max-h-[calc(100vh-140px)] overflow-y-auto')}>
      <table className="gt w-full font-data text-[14px]" onMouseLeave={() => setHoverCol(null)}>
        {/* The shared header, not a second copy of it. GTable used to respell
            table-kit's thead classes, so making every table's header heavier
            changed every table EXCEPT the ones drawn by this component - which
            is most of them. Sticky and the surface are this table's own; the
            type comes from the one place it is defined. */}
        <THead className="sticky top-0 z-10 bg-surface-subtle">
          <tr className="backdrop-blur">
            {cols.map((c, ci) => (
              <th key={c.key}
                style={(c.width || c.minWidth || c.fill) ? { width: c.fill ? '100%' : c.width, minWidth: c.minWidth } : undefined}
                title={tipOf(c) || undefined}
                className={cn('select-none px-4 py-2 align-bottom font-medium leading-[1.3]',
                  stickyFirst && ci === 0 && 'sticky left-0 z-20 bg-surface-subtle shadow-[1px_0_0_hsl(var(--border))]',
                  colLines && ci > 0 && 'border-l border-[hsl(var(--border)/0.75)]',
                  headNowrap && 'whitespace-nowrap',
                  c.align === 'right' && 'text-right',
                  c.sortable !== false && 'cursor-pointer hover:text-foreground',
                  sort === c.key && 'text-foreground',
                  tipOf(c) && c.sortable === false && 'cursor-help')}
                onClick={() => onSort(c.key, c.sortable)}>
                {/* An explanation nobody can find is not an explanation.
                    These tips were already written - every one of them - and
                    rendered only as a native title attribute: no icon to say
                    help exists, a second of hover before it appears, and
                    nothing at all on a touch screen. So the column that needed
                    explaining looked identical to the column that did not.
                    One (i) here makes every tip in every GTable report
                    discoverable at once. */}
                <span className={cn('inline-flex items-center gap-1', c.align === 'right' && 'justify-end')}>
                  <span className="inline-flex items-center gap-0.5">
                    {sort === c.key && c.align === 'right' && (dir === 'desc' ? <ChevronDown className="size-3" /> : <ChevronUp className="size-3" />)}
                    {/* GA4's dotted underline: the sign that a definition exists. */}
                    <span title={c.help ? (tipOf(c) || undefined) : undefined}
                      className={cn(tipOf(c) && 'border-b border-dotted border-muted-foreground/60 pb-px')}>{c.label}</span>
                    {sort === c.key && c.align !== 'right' && (dir === 'desc' ? <ChevronDown className="size-3" /> : <ChevronUp className="size-3" />)}
                  </span>
                  {/* A column that explains its calculation gets the ? and its
                      panel (the one-line tip shows on the name); the rest keep (i). */}
                  {c.help ? (
                    <ColumnHelp label={c.label} tip={tipOf(c)} help={c.help} rows={visible} />
                  ) : tipOf(c) && (
                    <span onClick={(e) => e.stopPropagation()} className="cursor-help">
                      <InfoTip size={3}>{tipOf(c)}</InfoTip>
                    </span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </THead>
        <tbody>
          {/* Two row shapes beyond a plain one, because some tables are not a
              flat list. `_group` draws a heading across the table - paid and
              earned channels are different kinds of thing and ranking them
              against each other is a false comparison. `_total` is a summary
              line, which must be told apart from data or a reader adds it in.
              Neither takes part in sorting: a subtotal that floats to the top
              because it has the largest number is nonsense. */}
          {visible.map((r, i) => (
            r._group ? (
              <tr key={`g${i}`} className="border-t border-border/60">
                <td colSpan={cols.length}
                  className="bg-surface-subtle px-4 py-2 text-[13px] font-medium first-letter:uppercase text-muted-foreground">
                  {r._group}
                </td>
              </tr>
            ) : (() => {
              const k = keyOf(r, i);
              const canOpen = !!expand && !r._total;
              const isOpen = canOpen && openRow === k;
              return (
                <React.Fragment key={k}>
                  <tr
                    onClick={canOpen ? () => setOpenRow(isOpen ? null : k) : undefined}
                    aria-expanded={canOpen ? isOpen : undefined}
                    data-row-key={canOpen ? String(k) : undefined}
                    className={cn('border-t transition-colors',
                      r._total
                        ? 'border-border bg-card font-medium'
                        : 'border-border-light bg-card hover:bg-surface-subtle',
                      canOpen && 'cursor-pointer',
                      isOpen && 'bg-surface-subtle')}>
                    {cols.map((c, ci) => (
                      <td key={c.key}
                        onMouseEnter={() => setHoverCol(ci)}
                        className={cn('px-4 py-2 align-middle text-[14px] font-normal leading-[1.4] text-foreground transition-colors',
                          stickyFirst && ci === 0 && 'sticky left-0 z-[1] bg-inherit shadow-[1px_0_0_hsl(var(--border-light))]',
                          colLines && ci > 0 && 'border-l border-[hsl(var(--border)/0.75)]',
                          ci === 0 && !r._total && 'font-medium text-ink-950 dark:text-ink-50',
                          c.align === 'right' && !r._total && 'font-medium',
                          c.align === 'right' && 'text-right tabular-nums whitespace-nowrap',
                          hoverCol === ci && ci > 0 && 'bg-surface-subtle')}>
                        {ci === 0 && canOpen ? (
                          <span className="flex items-center gap-1.5">
                            <ChevronDown className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform',
                              !isOpen && '-rotate-90')} />
                            <span className="min-w-0">{c.render ? c.render(r) : r[c.key]}</span>
                          </span>
                        ) : (c.render ? c.render(r) : r[c.key])}
                      </td>
                    ))}
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-border-light">
                      <td colSpan={cols.length} className="bg-surface-subtle p-0">
                        {/* A wide table scrolls sideways, and the drill-down
                            cell is as wide as the table - so its content ran
                            off the card. Pinned to the left edge of what is on
                            screen and as wide as what is on screen (viewW), so
                            the detail stays put wherever the table is scrolled
                            to. Indented to the row's own text (cell padding 16
                            + chevron 14 + gap 6 = 36px), so the detail reads as
                            belonging to that row and never sits on the table's
                            edge. Callers add no padding of their own. Nothing
                            animates: the row below simply moves down once. */}
                        <div className="sticky left-0 pb-4 pl-9 pr-4 pt-1"
                          style={{ width: viewW ? `${viewW}px` : '100%' }}>
                          {expand(r)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })()
          ))}
          {!sorted.length && (
            <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-xs text-muted-foreground">{empty}</td></tr>
          )}
        </tbody>
      </table>
      {canPreview && (
        <button type="button" onClick={() => setSheetOpen(true)}
          className="flex w-full items-center justify-between border-t border-border bg-muted/30 px-3 py-2 text-[13px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground">
          <span>Showing top {preview} of {sorted.length}</span>
          <span className="font-medium">View all &rsaquo;</span>
        </button>
      )}
      {canPreview && (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-4xl">
            <SheetHeader className="border-b border-border px-5 py-4">
              <SheetTitle className="flex items-center gap-2 text-sm font-semibold">
                {sheetTitle || 'All rows'}
                {sheetChip && <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[13px] font-medium text-muted-foreground">{sheetChip}</span>}
              </SheetTitle>
            </SheetHeader>
            <div className="px-5 pt-4">
              <label className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
                <Search className="size-3.5 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..."
                  className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground" />
              </label>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <GTable cols={cols} rows={sheetRows} initSort={sort} initDir={dir} empty="Nothing matches that search." />
            </div>
            <div className="border-t border-border px-5 py-3 text-[13px] text-muted-foreground">
              Showing {sheetRows.length.toLocaleString('en-IN')} of {sorted.length.toLocaleString('en-IN')}
            </div>
          </SheetContent>
        </Sheet>
      )}
      </div>
    </div>
  );
}

// ── Section shell: eyebrow + title + optional aside, consistent rhythm ─────
export function GSection({ eyebrow, title, sub, aside, children, tone, wide, compact, line }) {
  // compact: the table IS the section. One title line, the explanation (and
  // the eyebrow, which only restated the title) behind the (i), an optional
  // one short `line` under it, and the body close under the title. Used where
  // a reader came for the rows: stacked eyebrow + title + paragraph put the
  // first row of a table 150px below its heading.
  if (compact) {
    const info = [sub].filter(Boolean);
    return (
      <Card className="bs-card overflow-hidden" tone={tone}>
        {/* The title band: the same soft tint as every Panel, so a report's
            sections start the way every other section in the app does. */}
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1.5 border-b border-[hsl(var(--border-light))] px-5 py-3.5">
          <h3 className="min-w-0 flex-1 basis-[220px] text-[15px] font-semibold leading-snug tracking-[-0.005em] text-foreground">
            {title}
            {info.length > 0 && (
              <>{' '}<MoreInfo label={typeof title === 'string' ? `About ${title.toLowerCase()}` : 'More about this'}>
                {info.map((t, i) => (typeof t === 'string' ? <p key={i} className={i ? 'mt-2' : undefined}>{t}</p> : <React.Fragment key={i}>{t}</React.Fragment>))}
              </MoreInfo></>
            )}
            {/* Qbee explains this section, starting from what is on screen. */}
            <ExplainButton title={title} className="ml-0.5 -translate-y-px" />
          </h3>
          {aside && <div className="ml-auto shrink-0">{aside}</div>}
          {line && <p className="basis-full text-[13.5px] leading-relaxed text-muted-foreground">{line}</p>}
        </div>
        <div className="p-5">{children}</div>
      </Card>
    );
  }
  // The title block runs WIDE (user call: name + description never wrap
  // early), with a firm gap holding the aside off it on the right. The
  // aside keeps its natural width; the text takes everything else.
  //
  // The basis is what decides whether the aside shares the row. At 420px the
  // text demanded more than a third-width card can give, so on those cards the
  // total dropped BELOW the description and sat left-aligned in the body while
  // the same total on a wide card sat top right - one component, two
  // placements, and the number stopped reading as the denominator its rows are
  // measured against.
  //
  // 180px is the honest floor: enough that a wrapped title still reads as a
  // sentence, low enough that a third-width card keeps its total on the header
  // row. Forbidding the wrap outright was worse - at 1024px the title
  // compressed to one word per line.
  //
  // When it does wrap, ml-auto keeps the aside right-aligned on its own row
  // rather than stranding it under the text.
  return (
    <Card className="bs-card overflow-hidden" tone={tone}>
      <div className="flex flex-wrap items-start justify-between gap-x-10 gap-y-2 border-b border-[hsl(var(--border-light))] px-5 py-3.5">
        <div className="min-w-0 flex-1 basis-[180px]">
          {eyebrow && <p className="t-eyebrow">{eyebrow}</p>}
          <h3 className={cn('text-[15px] font-semibold leading-snug tracking-[-0.005em] text-foreground', eyebrow && 'mt-1')}>{title}<ExplainButton title={title} className="ml-1 -translate-y-px" /></h3>
          {sub && <Lede text={sub} className="mt-0.5 pr-2 text-[13.5px] leading-relaxed text-muted-foreground" />}
        </div>
        {aside && <div className="ml-auto shrink-0">{aside}</div>}
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

// ── SectionDeck: a run of GSections as a deck ───────────────────────────────
// A report page used to be five or six full cards in a column. Wrap the run
// in SectionDeck and each becomes a collapsed card on the left with the
// chosen one open on the right. Grids and conditionals between the sections
// are looked through; anything that is not a section renders above the deck.
export function SectionDeck({ storageKey, children }) {
  const items = [];
  const extras = [];
  const holds = (node) => {
    if (node == null || typeof node !== 'object') return false;
    if (Array.isArray(node)) return node.some(holds);
    if (!React.isValidElement(node)) return false;
    if (node.type === GSection) return true;
    return holds(node.props?.children);
  };
  const walk = (node) => {
    if (node == null || node === false || node === true) return;
    if (Array.isArray(node)) { node.forEach(walk); return; }
    if (!React.isValidElement(node)) { extras.push(node); return; }
    if (node.type === GSection) {
      const { eyebrow, title, sub, aside, tone, wide, children: body } = node.props;
      const key = node.key || `${typeof title === 'string' ? title : 'section'}-${items.length}`;
      items.push({ key, eyebrow, title, sub, aside, wide, chip: tone ? <span className={cn('size-2 rounded-full', tone === 'good' ? 'bg-emerald-500' : tone === 'attention' ? 'bg-amber-500' : tone === 'critical' ? 'bg-rose-500' : 'bg-blue-500')} /> : null, render: () => body });
      return;
    }
    if ((node.type === React.Fragment || typeof node.type === 'string') && holds(node.props?.children)) { walk(node.props.children); return; }
    extras.push(node);
  };
  walk(children);
  return (
    <div className="space-y-5">
      {extras.length > 0 && <div className="space-y-5">{extras.map((e, i) => <React.Fragment key={i}>{e}</React.Fragment>)}</div>}
      <CardDeck storageKey={storageKey} items={items} />
    </div>
  );
}

// ── Denominator stat: the total a card's rows slice up ─────────────────────
// Every distribution table invites the same question - "out of how many?" -
// and the answer used to live in a stray line above the page or nowhere.
// Pinned to the card's top right so every row reads against it.
export function GTotal({ label, value, sub }) {
  if (value == null) return null;
  return (
    <div className="ml-auto shrink-0 text-right">
      <p className="text-[13px] font-medium first-letter:uppercase text-muted-foreground">{label}</p>
      <p className="text-[22px] font-medium leading-tight tracking-[-0.02em] tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-[14px] tabular-nums text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ── Connected footer: every Growth page names its neighbours ───────────────
/**
 * A report that could not honour the window it was given says so, here.
 *
 * Several reports floor the window - a return curve needs a fortnight of spend
 * at different levels before it means anything - and they used to apply that
 * floor in silence. The page then printed a week of numbers under a header
 * that said Today. The floor is defensible; not saying it is not.
 */
export function GWindowNote({ window: w }) {
  if (!w || !w.widened || !w.note) return null;
  return (
    <p className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-50 px-3 py-2 text-[13px] leading-relaxed text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
      <Info className="mt-0.5 size-3.5 shrink-0" />
      <span>{w.note}</span>
    </p>
  );
}

// ── GWhy: the defence of a number, one click away ──────────────────────────
//
// A report has to be able to defend what it prints, and the defence is often a
// paragraph: which clock each side counts on, what the window does to the
// comparison, what a gap is and is not. Printed inline, five of those turn a
// page into an essay and the numbers stop being read at all. Deleted, the
// number is unfalsifiable. So it lives here - a line by default, the whole
// argument when the reader asks for it - and the summary line above it carries
// the one sentence that matters.
export function GWhy({ label = 'Why these differ', children, className, open = false }) {
  return (
    <details open={open} className={cn('group', className)}>
      <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground [&::-webkit-details-marker]:hidden">
        <ChevronRight className="size-3.5 shrink-0 transition-transform group-open:rotate-90" />
        {label}
      </summary>
      <div className="mt-1.5 max-w-[78ch] space-y-2 text-[13.5px] leading-relaxed text-muted-foreground">{children}</div>
    </details>
  );
}

export function ConnectedFooter({ links = [] }) {
  // Inside an explorer the frame's own footer says where to go next.
  const embedded = useEmbedded();
  if (embedded) return null;
  return (
    <p className="text-[14px] text-muted-foreground">
      Connected:{' '}
      {links.map((l, i) => (
        <React.Fragment key={l.to}>
          {i > 0 && ' · '}
          <Link className="text-primary underline-offset-2 hover:underline" to={l.to}>{l.label}</Link>
        </React.Fragment>
      ))}
    </p>
  );
}
